/** Aviso de lo ya emitido que aún no tiene asiento (facturas, cobros, provisiones) y botón para contabilizarlo. */
import { useCallback, useEffect, useState } from 'react'
import { AlertCircle, Loader2 } from 'lucide-react'
import { api } from '../../lib/api'
import { useAuth } from '../../contexts/AuthContext'
import { apiMessage } from './EntryModal'
import type { CtbBackfillResult, CtbStatusDto } from '@toga/shared'

export default function PendingBanner({ issuerId }: { issuerId: string }) {
  const auth = useAuth()
  const isAdmin = auth.status === 'authenticated' && auth.user.role === 'ADMIN'
  const [pending, setPending] = useState<CtbStatusDto['pending'] | null>(null)
  const [busy, setBusy] = useState(false)
  const [errors, setErrors] = useState<CtbBackfillResult['errors']>([])
  const [failure, setFailure] = useState<string | null>(null)

  const load = useCallback(() => {
    if (!issuerId) return
    api
      .get<CtbStatusDto>(`/ctb/status?issuerId=${issuerId}`)
      .then((s) => setPending(s.pending))
      .catch(() => setPending(null))
  }, [issuerId])
  useEffect(load, [load])

  if (!pending) return null
  const total = pending.invoices + pending.payments + pending.provisions
  if (total === 0 && errors.length === 0) return null

  const parts = [
    pending.invoices > 0 && `${pending.invoices} factura${pending.invoices === 1 ? '' : 's'}`,
    pending.payments > 0 && `${pending.payments} cobro${pending.payments === 1 ? '' : 's'}`,
    pending.provisions > 0 &&
      `${pending.provisions} provisión${pending.provisions === 1 ? '' : 'es'} de fondos`,
  ].filter(Boolean)

  const run = async () => {
    setBusy(true)
    setFailure(null)
    try {
      const r = await api.post<CtbBackfillResult>('/ctb/backfill', { issuerId })
      setErrors(r.errors)
      if (r.errors.length === 0) window.location.reload()
      else load()
    } catch (err) {
      setFailure(apiMessage(err, 'No se pudo contabilizar'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="text-xs rounded-lg px-3 py-2.5 border text-amber-200 bg-amber-500/10 border-amber-500/30 space-y-1.5 max-w-3xl">
      <div className="flex flex-wrap items-center gap-2">
        <AlertCircle size={13} className="shrink-0" />
        {total > 0 ? (
          <span>
            Hay {parts.join(', ')} emitidos que todavía no están en el diario. Se contabilizan con
            su fecha y, a partir de ahora, cada documento nuevo deja su asiento solo.
          </span>
        ) : (
          <span>Algunos documentos no se han podido contabilizar:</span>
        )}
        {isAdmin && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void run()}
            className="ml-auto px-2.5 py-1 rounded-lg text-xs font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {busy && <Loader2 size={12} className="animate-spin" />}
            Contabilizar ahora
          </button>
        )}
      </div>
      {failure && <p className="text-rose-300">{failure}</p>}
      {errors.length > 0 && (
        <ul className="list-disc pl-6 text-rose-300">
          {errors.map((e, i) => (
            <li key={i}>
              {e.ref}: {e.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
